import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import '../styles/Animation.css';

import {
  fetchAgents,
  addAgent,
  updateAgent,
  deleteAgent,
  toggleAgentBlacklist,
  fetchMergeConflicts
} from '../api/agentService';

function TableInsolvencyAgents() {
  const navigate = useNavigate();


  const [agents, setAgents] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  
  const [page, setPage] = useState(1);
  const [limit] = useState(30);
  
  const [searchTerm, setSearchTerm] = useState('');
  
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [deleteWarningMessage, setDeleteWarningMessage] = useState('');
  const [actionMenuPosition, setActionMenuPosition] = useState({ x: 0, y: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddMode, setIsAddMode] = useState(true);
  const [modalAgent, setModalAgent] = useState(null);
  const [modalError, setModalError] = useState('');
  const [modalForm, setModalForm] = useState({
    title: '',
    firstName: '',
    lastName: '',
    street: '',
    postalCode: '',
    city: '',
    country: '',
    email: ''
  });
  
  const [agentFilter, setAgentFilter] = useState('active');
  
  const [filteredCount, setFilteredCount] = useState(0);
  const [blacklistedCount, setBlacklistedCount] = useState(0);

  const [openActionMenu, setOpenActionMenu] = useState(null);
  
  const actionMenuRef = useRef(null);
  const actionButtonsRef = useRef({});

  const tableContainerRef = useRef(null);

  const [mergeConflictsCount, setMergeConflictsCount] = useState(null);
  const [loadingConflicts, setLoadingConflicts] = useState(true);

  const [isPageLoading, setIsPageLoading] = useState(false);

  const prevPageRef = useRef(page);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target) &&
        !Object.values(actionButtonsRef.current).some(ref => ref && ref.contains(event.target))
      ) {
        setOpenActionMenu(null);
      }
    }
  
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
  
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);
  
  const loadAgents = useCallback(() => {
    setIsPageLoading(true);
    
    const showBlacklisted = agentFilter !== 'active';
    const blacklistOnly = agentFilter === 'blacklisted';
    
    const noBlacklist = !showBlacklisted ? 'true' : 'false';
    
    fetchAgents(page, limit, searchTerm, noBlacklist, blacklistOnly)
      .then((data) => {
        let agentsList = [];
        let total = 0;
        
        if (Array.isArray(data.items)) {
          agentsList = data.items;
          total = data.total_count || 0;
        } else {
          agentsList = data;
          total = data.length;
        }
        
        if (blacklistOnly) {
          agentsList = agentsList.filter(agent => agent.blacklist);
        }
        
        setAgents(agentsList);
        setTotalCount(total);
        setFilteredCount(agentsList.length);
        
        if (agentFilter === 'all') {
          const blacklisted = agentsList.filter(agent => agent.blacklist).length;
          setBlacklistedCount(blacklisted);
        }
      })
      .catch((err) => {
        console.error('Error fetching agents:', err);
        setError('Could not fetch agents from server.');
      })
      .finally(() => {
        setTimeout(() => {
          setIsPageLoading(false);
      
          if (prevPageRef.current !== page) {
            const tableElement = document.querySelector('.insolvency-container');
            if (tableElement) {
              tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
      
          prevPageRef.current = page;
        }, 100);
      });
  }, [agentFilter, page, limit, searchTerm]);

  useEffect(() => {
    function handleScroll() {
      setOpenActionMenu(null);
    }
  
    window.addEventListener('scroll', handleScroll, true);
  
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);  

  useEffect(() => {
    if (page > 1 && agents.length === 0 && totalCount > 0) {
      setPage(prev => Math.max(prev - 1, 1));
    }
  }, [agents, totalCount, page]);

  useEffect(() => {
    const loadMergeConflictsCount = async () => {
      try {
        setLoadingConflicts(true);
        const data = await fetchMergeConflicts();
        setMergeConflictsCount(data.conflicts.length);
        setLoadingConflicts(false);
      } catch (err) {
        console.error("Error fetching merge conflicts count:", err);
        setMergeConflictsCount(0);
        setLoadingConflicts(false);
      }
    };

    loadMergeConflictsCount();
  }, []);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  useEffect(() => {
    if (message || error) {
      setIsNotificationVisible(true);
      
      const timer = setTimeout(() => {
        setIsNotificationVisible(false);
        
        setTimeout(() => {
          if (message) setMessage('');
          if (error) setError('');
        }, 500);
      }, 4000);
      
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  const handleToggleBlacklist = (agent) => {
    setError('');
    setMessage('');
    
    toggleAgentBlacklist(agent.agent_id)
      .then((response) => {
        setMessage(
          response.blacklist
            ? `${agent.name} added to blacklist successfully`
            : `${agent.name} removed from blacklist successfully`
        );
        
        loadAgents();
      })
      .catch((err) => {
        console.error('Error toggling blacklist status:', err);
        setError(err.message || 'Error updating blacklist status.');
      });
  };
  const handleFilterChange = (filterValue) => {
    setAgentFilter(filterValue);
    setPage(1);
  };

  const parseAddressString = (addressString) => {
    let street = '', postalCode = '', city = '', country = '';
    
    if (addressString) {
      const parts = addressString.split(',');
      street = parts[0] ? parts[0].trim() : '';
      
      if (parts[1]) {
        const cityPart = parts[1].trim();
        const match = cityPart.match(/^(\d+)\s+(.+)$/);
        
        if (match) {
          postalCode = match[1];
          city = match[2];
        } else {
          city = cityPart;
        }
      }
      
      country = parts[2] ? parts[2].trim() : '';
    }
    
    return { street, postalCode, city, country };
  };

  const validateAddress = (street, postalCode, city, country) => {
    street = street.trim();
    postalCode = postalCode.trim();
    city = city.trim();
    country = country.trim();
    
    const fields = [street, postalCode, city, country];
    const filledCount = fields.filter(f => f).length;
    
    if (filledCount > 0 && filledCount < 4) {
      return {
        valid: false,
        error: 'Please complete all address fields or leave them all blank.'
      };
    }
    
    if (filledCount === 0) {
      return { valid: true };
    }
    
    if (!/^\d{5}$/.test(postalCode)) {
      return {
        valid: false,
        error: 'German postal code must be exactly 5 digits.'
      };
    }
    
    const streetRegex = /^[a-zA-ZäöüÄÖÜß\s\-\.]+\s*[\d\-\/]*\s*[a-zA-Z]*$/;
    if (!streetRegex.test(street)) {
      return {
        valid: false,
        error: 'Invalid street format. (e.g. "Musterstraße 123")'
      };
    }
    
    const cityRegex = /^[a-zA-ZäöüÄÖÜß\s\-\.]+$/;
    if (!cityRegex.test(city)) {
      return {
        valid: false,
        error: 'City name should contain only letters, spaces, and hyphens.'
      };
    }
    
    return { valid: true };
  };

  const formatAddressString = (street, postalCode, city, country) => {
    street = street.trim();
    postalCode = postalCode.trim();
    city = city.trim();
    country = country.trim();
    
    const addressFields = [street, postalCode, city, country];
    const filledAddressFields = addressFields.filter(field => field !== '');
    
    if (filledAddressFields.length === 4) {
      return `${street}, ${postalCode} ${city}, ${country}`;
    }
    
    return '';
  };

  const resetModal = () => {
    setModalForm({
      title: '',
      firstName: '',
      lastName: '',
      street: '',
      postalCode: '',
      city: '',
      country: '',
      email: ''
    });
    setModalError('');
    setModalAgent(null);
  };

  const handleOpenAddModal = () => {
    setError('');
    setMessage('');
    resetModal();
    setIsAddMode(true);
    setIsModalOpen(true);
  };

  const handleEdit = (agent) => {
    setError('');
    setMessage('');
    setModalError('');
    setIsAddMode(false);
    setModalAgent(agent);
    
    const [first, ...rest] = agent.name ? agent.name.split(' ') : ['', ''];
    const last = rest.join(' ');
    
    const addressComponents = parseAddressString(agent.address);
    
    setModalForm({
      title: agent.title || '',
      firstName: first || '',
      lastName: last || '',
      street: addressComponents.street,
      postalCode: addressComponents.postalCode,
      city: addressComponents.city,
      country: addressComponents.country,
      email: agent.email || ''
    });
    
    setIsModalOpen(true);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    resetModal();
  };

  const handleModalFormChange = (e) => {
    setModalForm({ ...modalForm, [e.target.name]: e.target.value });
  };

  const validateModalForm = () => {
    if (!modalForm.title.trim()) {
      return 'Title is required.';
    }
    if (!modalForm.firstName.trim() || !modalForm.lastName.trim()) {
      return 'Both first name and last name are required.';
    }
    if (isAddMode && !modalForm.email.trim()) {
      return 'Email is required when adding an agent.';
    }
    if (modalForm.email) {
      const emailRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(modalForm.email.trim())) {
        return 'Invalid email format.';
      }
    }
    const addressValidation = validateAddress(
      modalForm.street,
      modalForm.postalCode,
      modalForm.city,
      modalForm.country
    );
    if (!addressValidation.valid) {
      return addressValidation.error;
    }
    return null;
  };

  const checkForDuplicates = (combinedName, trimmedTitle, addressString) => {
    const duplicates = agents.filter(a => {
      if (!isAddMode && modalAgent && a.agent_id === modalAgent.agent_id) {
        return false;
      }
      return (
        a.title?.trim().toLowerCase() === trimmedTitle.toLowerCase() &&
        a.name?.trim().toLowerCase() === combinedName.toLowerCase()
      );
    });
    
    if (duplicates.length === 0) {
      return null;
    }
    
    if (addressString === '') {
      return 'An agent with the same Title and Name already exists. Provide a complete address to differentiate.';
    }
    
    const addressDuplicate = duplicates.find(d => {
      const duplicateAddress = d.address ? d.address.trim().toLowerCase() : '';
      return duplicateAddress === addressString.toLowerCase();
    });
    if (addressDuplicate) {
      return 'An agent with the same Title, Name, and Address already exists.';
    }
    
    const duplicateWithoutAddress = duplicates.find(d => !d.address || d.address.trim() === '');
    if (duplicateWithoutAddress) {
      return 'A similar agent without an address exists. Both must have addresses to differentiate.';
    }
    
    return null;
  };

  const handleModalSave = () => {
    setModalError('');
    
    const validationError = validateModalForm();
    if (validationError) {
      setModalError(validationError);
      return;
    }
    
    const combinedName = `${modalForm.firstName.trim()} ${modalForm.lastName.trim()}`;
    const trimmedTitle = modalForm.title.trim();
    const addressString = formatAddressString(
      modalForm.street.trim(),
      modalForm.postalCode.trim(),
      modalForm.city.trim(),
      modalForm.country.trim()
    );
    
    const duplicateError = checkForDuplicates(combinedName, trimmedTitle, addressString);
    if (duplicateError) {
      setModalError(duplicateError);
      return;
    }
    
    const payload = {
      title: trimmedTitle,
      name: combinedName,
      address: addressString,
      email: modalForm.email.trim()
    };
    
    if (isAddMode) {
      addAgent(payload)
        .then(() => {
          setMessage('Agent added successfully!');
          setIsModalOpen(false);
          setPage(1);
          loadAgents();
        })
        .catch((err) => {
          setModalError(err.message || 'Error adding agent.');
        });
    } else {
      updateAgent(modalAgent.agent_id, payload)
        .then(() => {
          setMessage('Agent updated successfully!');
          setIsModalOpen(false);
          resetModal();
          loadAgents();
        })
        .catch((err) => {
          if (err.message && (
              err.message.includes('duplicate') ||
              err.message.includes('already exists')
            )) {
            setModalError('Another agent with the same Title, Name, and Address already exists.');
          } else {
            setModalError(err.message || 'Error updating agent.');
          }
        });
    }
  };

  const confirmDelete = async (agent) => {
    setError('');
    setMessage('');
  
    try {
      const response = await fetch(`/api/agents/${agent.agent_id}/usage-count`);
  
      if (!response.ok) {
        throw new Error('Failed to check agent usage');
      }
  
      const data = await response.json();
      const agentFullName = `${agent.title ? agent.title + ' ' : ''}${agent.name}`.trim();
  
      let warningMessage;
      if (data.count > 0) {
        warningMessage = `${agentFullName} is linked to ${data.count} insolvency case${
          data.count > 1 ? 's' : ''
        }. Deleting this agent will affect these cases. Do you really want to proceed?`;
      } else {
        warningMessage = `Do you really want to delete ${agentFullName}?`;
      }
  
      setAgentToDelete(agent);
      setDeleteWarningMessage(warningMessage);
      setIsDeleteModalOpen(true);
  
    } catch (error) {
      console.error('Error retrieving agent usage:', error);
  
      const agentFullName = `${agent.title ? agent.title + ' ' : ''}${agent.name}`.trim();
      setAgentToDelete(agent);
      setDeleteWarningMessage(
        `Do you really want to delete ${agentFullName}? (Could not verify if the agent is in use.)`
      );
      setIsDeleteModalOpen(true);
    }
  };
  
  const handleDeleteConfirm = () => {
    if (!agentToDelete) return;
  
    deleteAgent(agentToDelete.agent_id)
      .then(() => {
        setMessage('Agent successfully deleted!');
        setIsDeleteModalOpen(false);
        setAgentToDelete(null);
        loadAgents();
      })
      .catch(() => {
        setError('Agent could not be deleted.');
      });
  };

  const toggleActionMenu = (agentId) => {
    setOpenActionMenu(openActionMenu === agentId ? null : agentId);
  };

  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleCSVDownload = () => {
    setMessage('');
    setError('');
    
    const showBlacklisted = agentFilter !== 'active';
    const blacklistOnly = agentFilter === 'blacklisted';
    const noBlacklist = !showBlacklisted ? 'true' : 'false';
    
    fetchAgents(1, 1000, searchTerm, noBlacklist, blacklistOnly)
      .then((data) => {
        let result = Array.isArray(data.items) ? data.items : data;
        if (blacklistOnly) {
          result = result.filter(agent => agent.blacklist);
        }
        
        if (result.length === 0) {
          setError('No agents to download.');
          return;
        }
        
        downloadAgentsAsCSV(result);
        setMessage('Agents downloaded successfully as CSV.');
      })
      .catch((err) => {
        console.error('Error downloading agents:', err);
        setError(err.message || 'Error downloading agents.');
      });
  };

  const downloadAgentsAsCSV = (agents) => {
    const headers = ['Title', 'Name', 'Address', 'Email', 'Blacklisted'];
    const csvRows = [
      headers.join(','),
      ...agents.map(agent => {
        const escapeCsvValue = (value) =>
          value ? `"${value.replace(/"/g, '""')}"` : '""';
        
        const title = escapeCsvValue(agent.title);
        const name = escapeCsvValue(agent.name);
        const address = escapeCsvValue(agent.address);
        const email = escapeCsvValue(agent.email);
        const blacklisted = agent.blacklist ? '"Yes"' : '"No"';
        
        return [title, name, address, email, blacklisted].join(',');
      })
    ];
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const filename = `insolvency_agents_${new Date().toISOString().slice(0, 10)}.csv`;
    
    triggerDownload(url, filename);
  };

  const triggerDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPages = Math.ceil(totalCount / limit);
  
  const handlePrevPage = () => {
    if (page > 1 && !isPageLoading) {
      setPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages && !isPageLoading) {
      setPage(prev => prev + 1);
    }
  };

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const calculateMenuPosition = (index, totalAgents) => {
    if (totalAgents === 1 || index === 0) {
      return {
        top: '100%',
        bottom: 'auto',
        right: '0',
        transform: 'translateY(0)'
      };
    }
    
    return {
      top: 'auto',
      bottom: '100%',
      right: '0', 
      transform: 'translateY(-5px)'
    };
  };

  return (
    <div className="insolvency-container" style={{ 
      width: '100%',
      maxWidth: 'none',
      margin: '0 auto',
      overflowX: 'auto',
      overflowY: 'visible'
    }}>
      <h2>
        Insolvency Agents
      </h2>
      
        {(message || error) && (
          <div className="message" 
            style={{opacity: isNotificationVisible ? 1 : 0,}}
          >
            {error || message}
          </div>
        )}

      {isDeleteModalOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>Delete Agent</h3>
            <p>{deleteWarningMessage}</p>
            <div className="popup-actions">
              <button
                className="button-accept"
                onClick={handleDeleteConfirm}
              >
                Yes, delete
              </button>
              <button
                className="button-cancel"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {isModalOpen && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-container" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '600px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ marginTop: 0, borderBottom: '1px solid #f0f0f0', paddingBottom: '12px' }}>
              {isAddMode ? 'Add Agent' : 'Edit Agent'}
            </h3>
            
            {modalError && (
              <div style={{
                marginBottom: '16px',
                padding: '10px',
                borderRadius: '4px',
                backgroundColor: '#fff2f0',
                border: '1px solid #ffccc7'
              }}>
                {modalError}
              </div>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ gridColumn: '1 / 3' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Title:<span style={{ color: '#cf1322' }}>*</span>
                </label>
                <select
                  name="title"
                  value={modalForm.title}
                  onChange={handleModalFormChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}
                >
                  <option value="">-- Select Title --</option>
                  <option value="Herr">Herr</option>
                  <option value="Herr Dr.">Herr Dr.</option>
                  <option value="Herr Dr. Prof.">Herr Dr. Prof.</option>
                  <option value="Frau">Frau</option>
                  <option value="Frau Dr.">Frau Dr.</option>
                  <option value="Frau Dr. Prof.">Frau Dr. Prof.</option>
                </select>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  First Name:<span style={{ color: '#cf1322' }}>*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  placeholder="e.g. Max"
                  value={modalForm.firstName}
                  onChange={handleModalFormChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Last Name:<span style={{ color: '#cf1322' }}>*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  placeholder="e.g. Mustermann"
                  value={modalForm.lastName}
                  onChange={handleModalFormChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}
                />
              </div>
              
              <div style={{ gridColumn: '1 / 3' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Street:
                </label>
                <input
                  type="text"
                  name="street"
                  placeholder="e.g. Musterstrasse 1"
                  value={modalForm.street}
                  onChange={handleModalFormChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Postal Code:
                </label>
                <input
                  type="text"
                  name="postalCode"
                  placeholder="e.g. 12345"
                  value={modalForm.postalCode}
                  onChange={handleModalFormChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  City:
                </label>
                <input
                  type="text"
                  name="city"
                  placeholder="e.g. Musterstadt"
                  value={modalForm.city}
                  onChange={handleModalFormChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}
                />
              </div>
              
              <div style={{ gridColumn: '1 / 3' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Country:
                </label>
                <input
                  type="text"
                  name="country"
                  placeholder="Deutschland"
                  value={modalForm.country}
                  onChange={handleModalFormChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}
                />
              </div>
              
              <div style={{ gridColumn: '1 / 3' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Email:{isAddMode && <span style={{ color: '#cf1322' }}>*</span>}
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="e.g. max.mustermann@gmail.com"
                  value={modalForm.email}
                  onChange={handleModalFormChange}
                  style={{ 
                    width: '100%', 
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}
                />
              </div>
            </div>
            
            <div style={{ 
              marginTop: '8px', 
              borderTop: '1px solid #f0f0f0',
              paddingTop: '16px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button 
                className="button-add-agent" 
                onClick={handleModalSave}
              >
                {isAddMode ? 'Add Agent' : 'Save Changes'}
              </button>
              <button
                className="button-cancel"
                onClick={handleModalCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ 
        background: '#ffffff', 
        borderRadius: '8px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginBottom: '1.5rem',
          padding: '0 0 1rem 0', 
          borderBottom: '1px solid #f0f0f0',
          alignItems: 'flex-start'
        }}>
          <div>
            <button 
              onClick={handleCSVDownload} 
              className="button-small"
              style={{ 
                marginBottom: '0.5rem',
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export Agents
            </button>
            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
              This will download all agents matching your current search criteria as a CSV file.
            </p>
          </div>
          <div>
          <div> 

          <button
            className="button-small"
            onClick={handleOpenAddModal}
            style={{ 
              marginBottom: '0.5rem',

              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '0.5rem',
              padding: '8px 16px',
              background: '#1890ff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none" 
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              width="16"
              height="16"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                d="M12 4.5v15m7.5-7.5h-15" 
              />
            </svg>
            Add Agent
          </button>
          </div> 
          <div>
          <button 
                onClick={() => navigate('/merge')} 
                className="button-small"
                style={{ 
                  marginBottom: '0.5rem',
                  padding: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v4m0 4h.01M12 5a7 7 0 100 14 7 7 0 000-14z"
                />
              </svg>
              ({mergeConflictsCount}) Conflicts regarding the agents.
              </button>
              </div>
            </div>

        </div>

        
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: '1rem',
          marginBottom: '1.5rem',
          padding: '0 0 1rem 0',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '1rem', 
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              minWidth: '320px',
              flexGrow: 1,
              maxWidth: '500px'
            }}>
              <div style={{ position: 'relative', width: '100%' }}>
              <input
                style={{ 
                  width: '100%', 
                  padding: '8px 8px 8px 36px',
                  borderRadius: '4px',
                  border: '1px solid #d9d9d9',
                  color: '#000',
                  fontSize: '14px',
                  fontWeight: '400',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
                type="text"
                placeholder="Search by name or title..."
                value={searchTerm}
                onChange={handleSearchTermChange}
              />
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="16" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  style={{
                    position: 'absolute',
                    left: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#555'
                  }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              minWidth: '250px'
            }}>
              <label style={{ marginRight: '0.5rem', whiteSpace: 'nowrap' }}>
                Agent Filter:
              </label>
              <select
                className="select-filter"
                value={agentFilter}
                onChange={(e) => handleFilterChange(e.target.value)}
              >
                <option value="active">Active Agents Only</option>
                <option value="all">All Agents</option>
                <option value="blacklisted">Blacklisted Only</option>
              </select>
            </div>
          </div>
          
          <div style={{ 
            fontSize: '0.9rem', 
            color: '#666', 
            backgroundColor: '#f9f9f9',
            padding: '8px 12px',
            borderRadius: '4px',
            display: 'inline-block'
          }}>
            {filteredCount > 0 ? (
              <>
                Showing {filteredCount}{' '}
                {agentFilter === 'all'
                  ? 'agents'
                  : agentFilter === 'active'
                  ? 'active agents'
                  : 'blacklisted agents'}
                {agentFilter === 'all' && blacklistedCount > 0 && ` (${blacklistedCount} blacklisted)`}
              </>
            ) : (
              'No agents found matching your criteria'
            )}
          </div>
        </div>
        <div style={{ 
          overflowX: 'auto',
          overflowY: 'visible',
          maxWidth: '100%',
          position: 'relative'
        }}>
          {isPageLoading && (
            <div style={{ 
              textAlign: 'center', 
              padding: '1rem',
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 10,
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div style={{ 
                width: '30px',
                height: '30px', 
                border: '3px solid rgba(0, 0, 0, 0.1)', 
                borderRadius: '50%', 
                borderTop: '3px solid #1890ff', 
                animation: 'spin 1s linear infinite' 
              }}></div>
            </div>
          )}
            <table>
            <colgroup>
              <col style={{ width: '25%' }} />
              <col style={{ width: '35%' }} />
              <col style={{ width: '30%' }} />
              <col style={{ width: '10%' }} />
            </colgroup>
            <thead>
              <tr style={{ backgroundColor: '#fafafa', borderBottom: '2px solid #f0f0f0' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#ffffff' }}>Title &amp; Name</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#ffffff' }}>Address</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#ffffff' }}>Email</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#ffffff' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent, index) => (
                <tr
                  key={agent.agent_id}
                  style={{
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                    borderBottom: '1px solid #f0f0f0',
                    transition: 'background-color 0.2s ease'
                  }}
                  className="agent-row"
                >
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {agent.blacklist && (
                        <span
                          title="This agent is blacklisted"
                          style={{
                            marginRight: '8px',
                            display: 'inline-block',
                            color: '#cf1322',
                            flexShrink: 0
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            width="16"
                            height="16"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                            />
                          </svg>
                        </span>
                      )}
                      <span style={agent.blacklist ? { color: '#999999' } : {}}>
                        {`${agent.title || ''} ${agent.name}`.trim()}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={agent.blacklist ? { color: '#999999' } : {}}>
                      {agent.address}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={agent.blacklist ? { color: '#999999' } : {}}>
                      {agent.email}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: '12px 16px',
                      textAlign: 'center',
                      position: 'relative'
                    }}
                  >
                    <div className="action-dropdown-container">
                      <button
                        className="button-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const menuHeight = 140;
                      
                          let posY = rect.bottom;
                          if (rect.bottom + menuHeight > window.innerHeight) {
                            posY = rect.top - menuHeight;
                          }
                      
                          setActionMenuPosition({
                            x: rect.right,
                            y: posY,
                            initialScrollY: window.scrollY,
                          });
                          toggleActionMenu(agent.agent_id);
                        }}
                        title={`Actions for ${agent.name}`}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          padding: '8px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          width="20"
                          height="20"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zm0 6a.75.75 0 110-1.5.75.75 0 010 1.5zm0 6a.75.75 0 110-1.5.75.75 0 010 1.5z"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {agents.length === 0 && (
                <tr>
                  <td colSpan="4" style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
                    No agents found matching your search criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {openActionMenu && (() => {
            const agent = agents.find(a => a.agent_id === openActionMenu);
            if (!agent) return null;

            return (
              <div
                ref={actionMenuRef}
                style={{
                  position: 'fixed',
                  top: actionMenuPosition.y,
                  left: actionMenuPosition.x - 150,
                  zIndex: 2000,
                  background: 'white',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  borderRadius: '4px',
                  minWidth: '180px',
                  fontSize: '14px'
                }}
              >
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  <li>
                    <button
                      onClick={() => {
                        handleEdit(agent);
                        setOpenActionMenu(null);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 12px',
                        width: '100%',
                        textAlign: 'left',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background 0.2s'
                      }}
                      title={`Edit ${agent.title} ${agent.name}`}
                      onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      Edit Agent
                    </button>
                  </li>

                  <li>
                    <button
                      onClick={() => {
                        handleToggleBlacklist(agent);
                        setOpenActionMenu(null);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 12px',
                        width: '100%',
                        textAlign: 'left',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background 0.2s'
                      }}
                      title={
                        agent.blacklist
                          ? `Remove ${agent.title} ${agent.name} from blacklist`
                          : `Add ${agent.title} ${agent.name} to blacklist`
                      }
                      onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      {agent.blacklist ? 'Remove from Blacklist' : 'Add to Blacklist'}
                    </button>
                  </li>

                  <li>
                    <button
                      onClick={() => {
                        confirmDelete(agent);
                        setOpenActionMenu(null);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 12px',
                        width: '100%',
                        textAlign: 'left',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background 0.2s',
                        color: '#cf1322'
                      }}
                      title={`Delete ${agent.title} ${agent.name}`}
                      onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#fff1f0')}
                      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      Delete Agent
                    </button>
                  </li>
                </ul>
              </div>
            );
          })()}
        </div>
      </div>

      <div
        className="pagination-container"
      >
        <button
          className="pagination-button"
          disabled={page <= 1 || isPageLoading}
          onClick={() => setPage(1)}
          title="First Page"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>

        <button
          className="pagination-button"
          disabled={page <= 1 || isPageLoading}
          onClick={handlePrevPage}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Previous
        </button>

        <div>
          <span>Page {page} of {totalPages || 1}</span>
        </div>

        <button
          className="pagination-button"
          disabled={page >= totalPages || totalPages === 0 || isPageLoading}
          onClick={handleNextPage}
        >
          Next
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <button
          className="pagination-button"
          disabled={page >= totalPages || totalPages === 0 || isPageLoading}
          onClick={() => setPage(totalPages)}
          title="Last Page"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7m-8-14l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default TableInsolvencyAgents;